import { DisplayButtons } from '@/components/DisplayButtons';
import {
    ELoanUsage,
    EUserFlow,
    selectEmployments,
    selectIsPreapproved,
    selectLoanUsage,
    setEmploymentIndex,
} from '@/context/borrower';
import {
    EDisplays,
    EJourneys,
    EModals,
    selectPartner,
    selectUserFlow,
    setDisplay,
    setModal,
} from '@/context/display';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';
import { selectIsLoggedIn } from '@/context/user';
import { MIN_EMPLOYMENT_YEAR } from '@/utils/constants';
import { getTotalEmploymentYears } from '@/utils/getTotalEmploymentYears';
import { isEmploymentComplete } from '@/utils/isEmploymentComplete';
import { EParameters, getParameters } from '@/utils/setParametersFromParams';

interface IEmploymentButtonsProps {
    setShowErrors: (value: boolean) => void;
    baseId?: string;
}

export function EmploymentButtons({
    setShowErrors,
    baseId,
}: IEmploymentButtonsProps): JSX.Element {
    // ***** Redux State *****
    const dispatch = useAppDispatch();
    const employments = useAppSelector(selectEmployments);
    const partner = useAppSelector(selectPartner);
    const { earlyReferredScreen, showQuickQuote } = partner;
    const isApproved = useAppSelector(selectIsPreapproved);
    const userFlow = useAppSelector(selectUserFlow);
    const loanUsage = useAppSelector(selectLoanUsage);
    const isLoggedIn = useAppSelector(selectIsLoggedIn);
    const isNovatedLease = loanUsage === ELoanUsage.NOVATED;
    const skipPreapproval = partner.skipPreapproval;

    const params = getParameters();

    const paramJourney = params.get(EParameters.INITIAL_JOURNEY);
    // ***** Event Handlers *****
    function handleBackClick(): void {
        if (paramJourney?.toLocaleUpperCase() === EJourneys.TEST_DRIVE) {
            dispatch(setDisplay(EDisplays.INCOME));
            return;
        }

        if (loanUsage === ELoanUsage.NOVATED) {
            dispatch(setDisplay(EDisplays.INCOME));
        } else {
            if (skipPreapproval) {
                dispatch(setDisplay(EDisplays.OCR_SUMMARY));
                return;
            }
            const backScreen = getBackScreen();
            dispatch(setDisplay(backScreen));
        }
    }

    function handleNextClick(): void {
        setShowErrors(true);
        if (!isComplete()) {
            // If employment is incomplete, automatically open the first incomplete employment modal
            const firstIncompleteIndex = employments.findIndex(
                (employment) =>
                    !isEmploymentComplete(employment, isNovatedLease)
            );
            if (firstIncompleteIndex !== -1) {
                dispatch(setEmploymentIndex(firstIncompleteIndex));
                dispatch(setModal(EModals.WORK_HISTORY));
            }
            return;
        }
        if (loanUsage === ELoanUsage.NOVATED) {
            if (!isLoggedIn) {
                dispatch(setModal(EModals.REGISTER));
                dispatch(setDisplay(EDisplays.IDENTIFICATION));
            } else {
                dispatch(setDisplay(EDisplays.IDENTIFICATION));
            }
        } else {
            const nextScreen = getNextScreen();
            dispatch(setDisplay(nextScreen));
        }
    }

    // ***** Helpers ******
    function isComplete(): boolean {
        let isComplete = true;
        employments.forEach((employment) => {
            if (!isEmploymentComplete(employment, isNovatedLease))
                isComplete = false;
        });
        if (getTotalEmploymentYears() < MIN_EMPLOYMENT_YEAR) isComplete = false;
        return isComplete;
    }

    function getNextScreen(): EDisplays {
        return EDisplays.ASSETS_AND_LIABILITIES;
    }

    function getBackScreen(): EDisplays {
        if (!isApproved && !earlyReferredScreen) return EDisplays.OCR_SUMMARY;
        if (userFlow === EUserFlow.CAMPAIGN) return EDisplays.OCR_SUMMARY;
        if (!showQuickQuote) return EDisplays.OCR_SUMMARY;

        return EDisplays.INDICATIVELY_APPROVED;
    }

    return (
        <DisplayButtons
            idLeftButton={`${baseId}-button-back`}
            idRightButton={`${baseId}-button-next`}
            rightClick={handleNextClick}
            leftClick={handleBackClick}
        />
    );
}
